import { ChallengeActivity, ChallengeReward, GetPointResponse, TopicCategory, UserTag } from '../types';

import { isValidUrl } from './urls';
import { makeColorIntoDataSvg } from './colors';

export function dupeTagsExist(newTagName: string, userTags: UserTag[]): string | undefined {
    let dupeTagName = '';

    userTags.some(tag => {
        if (tag.name.trim() === newTagName.trim()) {
            dupeTagName = tag.name;
            return true;
        }
        if (tag.tags && tag.tags.length > 0) {
            tag.tags.some(subtag => {
                if (subtag.name === newTagName) {
                    dupeTagName = subtag.name;
                    return true;
                }
                return false;
            });
        }
        return false;
    });

    if (dupeTagName.length > 0) {
        return dupeTagName;
    }

    return undefined;
}


export function numCategoryPoints(category_id: string | undefined, categories: TopicCategory[], points: GetPointResponse[]): number | undefined {
    if (!category_id) {
        return undefined;
    }

    // Verify the category actually exists in the categories list
    const categoryExists = categories.some(cat => cat._id?.$oid === category_id);
    if (!categoryExists) {
        return undefined; // Category doesn't exist, we can't be sure
    }

    // Get point counts by category
    const pointCountsByCategory: Map<string, number> = getPointCountsByCategory(points);

    // If category exists but has no entry in the map, it definitely has 0 points
    const numPointsInCategory = pointCountsByCategory.get(category_id);

    // Return 0 for confirmed zero points, or the actual count
    return numPointsInCategory ?? 0;
}

function getPointCountsByCategory(points: GetPointResponse[]) {
    const countMap = new Map<string, number>();

    const categoryIds = points.map(p => p.topic_category_id.$oid);

    categoryIds.forEach((category_id) => {
        if (!countMap.has(category_id)) {
            countMap.set(category_id, 0);
        }
    });

    points.forEach((p) => {
        const category_id = p.topic_category_id.$oid;

        if (!countMap.has(category_id)) {
            countMap.set(category_id, 0);
        }

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        countMap.set(category_id, countMap.get(category_id)! + 1);
    });

    return countMap;
}


export async function validateIcon(icon: string): Promise<string | undefined> {
    if(icon.startsWith('#')) {
        const hexRegex =/^#([0-9a-f]{3}){1,2}$/i;

        if (hexRegex.test(icon)) {
            return makeColorIntoDataSvg(icon);

        };
        throw new Error('Invalid hex for icon');
    } else if(icon.startsWith('data:image/svg+xml')) {
        return icon;
    } else if (icon.startsWith('/i/')) {
        return icon;
    }

    try {
        const urlIsValid = await isValidUrl(icon);
        return urlIsValid ? icon : undefined;
    } catch(e) {
        return undefined;
    }
}

export function dupeCategoriesExist(existingCategories: TopicCategory[], newCategoryName: string): string | undefined {
    let dupeCategoryName;

    existingCategories.some(category => {
        if (category.name === newCategoryName) {
            dupeCategoryName = category.name;
            return true;
        }
        return undefined;
    });

    return dupeCategoryName;
}

export function getPointsWithTags(tags: UserTag[], points: GetPointResponse[]): GetPointResponse[] {
    return points.filter((point) => {
        return point.tag_info?.user_tags.some((pointsTag) => {
            return tags.some((tag) => {
                return pointsTag._id?.$oid === tag._id?.$oid;
            });
        });
    });
}

export function instanceOfTagGroup(object: any): object is UserTag {
    return object.tags !== undefined && object.tags instanceof Array;
}

export function moveItem(array: any[], oldIndex: number, newIndex: number) {
    if (newIndex >= array.length) {
        newIndex = array.length - 1;
    }
    array.splice(newIndex, 0, array.splice(oldIndex, 1)[0]);
    return array;
};

export function dupeChallengeRewardsExist(existingChallengeRewards: ChallengeReward[], newChallengeRewardName: string): string | undefined {
    let dupeChallengeRewardName;

    existingChallengeRewards.some(challengeReward => {
        if (challengeReward.title === newChallengeRewardName) {
            dupeChallengeRewardName = challengeReward.title;
            return true;
        }
        return undefined;
    });

    return dupeChallengeRewardName;
}

export function dupeChallengeActivitysExist(existingChallengeActivities: ChallengeActivity[], newChallengeActivityName: string): string | undefined {
    let dupeChallengeActivityName;

    existingChallengeActivities.some(challengeActivity => {
        if (challengeActivity.name === newChallengeActivityName) {
            dupeChallengeActivityName = challengeActivity.name;
            return true;
        }
        return undefined;
    });

    return dupeChallengeActivityName;
}